import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, X, MapPin, Star, Users, BookOpen, GraduationCap, Heart } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default markers in leaflet
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Component to fit bounds when markers change
function FitBounds({ markers }) {
  const map = useMap();
  
  useEffect(() => {
    if (markers.length > 0) {
      const validMarkers = markers.filter(m => m.latitude && m.longitude && m.latitude !== 0 && m.longitude !== 0);
      if (validMarkers.length > 0) {
        const bounds = L.latLngBounds(validMarkers.map(m => [m.latitude, m.longitude]));
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12 });
      }
    }
  }, [markers, map]);
  
  return null;
}

const typeColors = {
  teacher: '#9B59B6',
  school: '#3498DB',
  service: '#D4A574',
  course: '#27AE60'
};

const typeIcons = {
  teacher: Users,
  school: BookOpen,
  service: Heart,
  course: GraduationCap
};

const typeLabels = {
  teacher: 'Profesionales',
  school: 'Escuelas',
  service: 'Servicios',
  course: 'Cursos'
};

export function MapView({ compact = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [cityFilter, setCityFilter] = useState('');
  const [selectedMarker, setSelectedMarker] = useState(null);
  const debounceRef = useRef(null);

  const types = [
    { key: null, label: 'Todos', icon: MapPin },
    { key: 'teacher', label: 'Profesionales', icon: Users },
    { key: 'school', label: 'Escuelas', icon: BookOpen },
    { key: 'service', label: 'Servicios', icon: Heart },
    { key: 'course', label: 'Cursos', icon: GraduationCap }
  ];

  const fetchMarkers = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/map/markers`;
      const params = [];
      if (selectedType) params.push(`type=${selectedType}`);
      if (cityFilter.trim()) params.push(`city=${encodeURIComponent(cityFilter.trim())}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setMarkers(response.data);
    } catch (error) {
      console.error('Error fetching markers:', error);
      setMarkers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarkers();
  }, [selectedType]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (cityFilter.length >= 2 || cityFilter.length === 0) {
        fetchMarkers();
      }
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [cityFilter]);

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
  };

  const handleViewDetails = () => {
    if (!selectedMarker) return;
    const routes = {
      teacher: `/teacher/${selectedMarker.id}`,
      school: `/school/${selectedMarker.id}`,
      service: `/service/${selectedMarker.id}`,
      course: `/course/${selectedMarker.id}`
    };
    navigate(routes[selectedMarker.type]);
  };

  const validMarkers = markers.filter(m => m.latitude && m.longitude && m.latitude !== 0 && m.longitude !== 0);
  const defaultCenter = [28.0, -16.5]; // Tenerife

  return (
    <div className={`bg-white rounded-2xl shadow-sm overflow-hidden ${compact ? '' : 'p-4'}`} data-testid="map-view">
      {/* Header & Filters */}
      <div className={compact ? 'p-4 pb-2' : 'mb-4'}>
        {!compact && (
          <h2 className="font-serif text-2xl font-bold text-primary mb-4 flex items-center gap-2">
            <MapPin className="h-6 w-6 text-gold" style={{ color: '#D4A574' }} />
            {t('map.title') || 'Mapa'}
          </h2>
        )}
        
        {/* Search Input */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder={t('map.searchCity') || 'Buscar por ciudad...'}
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="pl-10 pr-8"
            data-testid="map-search-input"
          />
          {cityFilter && (
            <button 
              onClick={() => setCityFilter('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Type Filters */}
        <div className="flex flex-wrap gap-2 mb-3">
          {types.map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.key || 'all'}
                onClick={() => setSelectedType(type.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedType === type.key
                    ? 'bg-gold text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={selectedType === type.key ? { backgroundColor: '#D4A574' } : {}}
                data-testid={`map-filter-${type.key || 'all'}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {type.label}
              </button>
            );
          })}
        </div>

        {/* Results Count */}
        <p className="text-sm text-gray-500">
          {validMarkers.length} {t('map.locationsFound') || 'ubicaciones encontradas'}
        </p>
      </div>

      {/* Map Container */}
      <div className={`relative ${compact ? 'h-64' : 'h-96'} rounded-xl overflow-hidden`}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-2" style={{ borderColor: '#D4A574', borderTopColor: 'transparent' }} />
              <p className="text-sm text-gray-500">{t('common.loading') || 'Cargando...'}</p>
            </div>
          </div>
        ) : (
          <MapContainer
            center={validMarkers.length > 0 ? [validMarkers[0].latitude, validMarkers[0].longitude] : defaultCenter}
            zoom={10}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds markers={validMarkers} />
            
            {validMarkers.map((marker) => (
              <CircleMarker
                key={`${marker.type}-${marker.id}`}
                center={[marker.latitude, marker.longitude]}
                radius={10}
                pathOptions={{
                  fillColor: typeColors[marker.type],
                  color: '#fff',
                  weight: 2,
                  opacity: 1,
                  fillOpacity: 0.8
                }}
                eventHandlers={{
                  click: () => handleMarkerClick(marker)
                }}
              >
                <Popup>
                  <div className="text-center min-w-[150px]">
                    <span 
                      className="inline-block px-2 py-0.5 rounded-full text-xs font-medium text-white mb-1"
                      style={{ backgroundColor: typeColors[marker.type] }}
                    >
                      {typeLabels[marker.type]}
                    </span>
                    <p className="font-semibold text-sm mb-0.5">{marker.title}</p>
                    {marker.city && (
                      <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                        <MapPin className="h-3 w-3" /> {marker.city}
                      </p>
                    )}
                    {marker.rating > 0 && (
                      <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" /> {marker.rating}
                      </p>
                    )}
                    <Button
                      size="sm"
                      className="mt-2 w-full bg-gold hover:bg-gold/90 text-white text-xs py-1"
                      style={{ backgroundColor: '#D4A574' }}
                      onClick={() => {
                        setSelectedMarker(marker);
                        handleViewDetails();
                      }}
                    >
                      Ver detalles
                    </Button>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        )}
      </div>

      {/* Legend */}
      <div className={`flex flex-wrap justify-center gap-4 ${compact ? 'p-3' : 'mt-4 pt-4 border-t'}`}>
        {Object.entries(typeColors).map(([type, color]) => {
          const Icon = typeIcons[type];
          return (
            <div key={type} className="flex items-center gap-1.5">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="text-xs text-gray-600">{typeLabels[type]}</span>
            </div>
          );
        })}
      </div>

      {/* Selected Marker Card (mobile) */}
      {selectedMarker && (
        <div className="fixed bottom-4 left-4 right-4 bg-white rounded-2xl shadow-xl p-4 z-[1000] md:hidden" data-testid="selected-marker-card">
          <button 
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            onClick={() => setSelectedMarker(null)}
          >
            <X className="h-5 w-5" />
          </button>
          <span 
            className="inline-block px-2 py-0.5 rounded-full text-xs font-medium text-white mb-2"
            style={{ backgroundColor: typeColors[selectedMarker.type] }}
          >
            {typeLabels[selectedMarker.type]}
          </span>
          <h3 className="font-semibold text-lg text-gray-900 mb-1">{selectedMarker.title}</h3>
          {selectedMarker.city && (
            <p className="text-sm text-gray-500 flex items-center gap-1 mb-3">
              <MapPin className="h-4 w-4" /> {selectedMarker.city}
            </p>
          )}
          <Button
            className="w-full bg-gold hover:bg-gold/90 text-white"
            style={{ backgroundColor: '#D4A574' }}
            onClick={handleViewDetails}
          >
            Ver Detalles
          </Button>
        </div>
      )}
    </div>
  );
}

export default MapView;
